<template>

  <div id="custom-styles-editor">

    <div class="top-buttons">
      <div class="back-button" @click="cancel()">
        <img src="@/assets/closedialogue.svg" />
      </div>
      <h3>Custom styles</h3>
      <p class="explainer" v-if="selected_item_details.length > 1">Showing the styles of the first selected item. Saving applies them to all {{ selected_item_details.length }} selected items.</p>
    </div>

    <div class="section" v-for="section in sections" :key="section.column">

      <p class="section-title">{{ section.label }}</p>

      <p class="empty" v-if="rows[section.column].length === 0">No styles yet.</p>

      <div class="row" v-for="(row, index) in rows[section.column]" :key="section.column + index"
        :class="{ 'row-invalid': !isAllowedDeclaration(row.property, row.value) }">

        <label>{{ propertyLabel(row.property) }}</label>

        <div class="control">

          <!-- colours: picker for hex values, plus text for rgba() etc. -->
          <template v-if="kind(row.property) === 'color'">
            <input type="color" :value="hexValue(row.value)" @input="setValue(section.column, row, $event.target.value)" />
            <input type="text" :value="row.value" @input="setValue(section.column, row, $event.target.value)" />
          </template>

          <!-- fonts: grouped picker; keeps the matching CSS fallback -->
          <select v-else-if="kind(row.property) === 'font'" :value="fontName(row.value)" @change="setFont(section.column, row, $event.target.value)">
            <option v-if="!knownFont(row.value)" :value="fontName(row.value)">{{ fontName(row.value) }}</option>
            <optgroup v-for="group in font_groups" :key="group.name" :label="group.name">
              <option v-for="font in group.fonts" :key="font" :value="font" :style="{ 'font-family': font }">{{ font }}</option>
            </optgroup>
          </select>

          <select v-else-if="choices(row.property)" :value="row.value" @change="setValue(section.column, row, $event.target.value)">
            <option v-if="!choices(row.property).includes(row.value)" :value="row.value">{{ row.value }}</option>
            <option v-for="choice in choices(row.property)" :key="choice" :value="choice">{{ choice }}</option>
          </select>

          <input v-else type="text" :value="row.value" @input="setValue(section.column, row, $event.target.value)" />

          <img class="remove" src="@/assets/delete_image.svg" @click="removeRow(section.column, index)" />

        </div>

        <p class="invalid-note" v-if="!isAllowedDeclaration(row.property, row.value)">This value isn't allowed and won't be saved.</p>

      </div>

      <div class="add-row">
        <select v-model="adding[section.column]">
          <option value="">Add property…</option>
          <option v-for="property in availableProperties(section.column)" :key="property" :value="property">{{ propertyLabel(property) }}</option>
        </select>
        <button v-if="adding[section.column]" @click="addRow(section.column)">Add</button>
      </div>

    </div>

    <ErrorNotice v-if="error" :message="error" />

    <div class="actions">
      <button class="secondary" @click="cancel()" :disabled="saving">Cancel</button>
      <button @click="save()" :disabled="saving">{{ saving ? 'Saving…' : 'Save' }}</button>
    </div>

  </div>

</template>


<script>
import ErrorNotice from './ErrorNotice'
import { ALLOWED_PROPERTIES, declarations, isAllowedDeclaration, loadFonts, toCss } from './menuCss'

const SECTIONS = [
  { column: 'item_css', label: 'Item' },
  { column: 'item_name_css', label: 'Name' },
  { column: 'item_description_css', label: 'Description' }
]
const COLOR_PROPERTIES = ['color', 'background-color', 'border-color', 'text-decoration-color']
const CHOICES = {
  'font-weight': ['300', '400', '500', '600', '700', '800', '900'],
  'font-style': ['normal', 'italic'],
  'font-variant': ['normal', 'small-caps'],
  'text-transform': ['none', 'uppercase', 'lowercase', 'capitalize'],
  'text-align': ['left', 'center', 'right'],
  'white-space': ['normal', 'nowrap'],
  'border-style': ['none', 'solid', 'dashed', 'dotted', 'double'],
  'text-decoration-style': ['solid', 'dashed', 'dotted', 'double', 'wavy']
}
const DEFAULT_VALUES = {
  color: '#000000', 'background-color': '#ffffff', 'border-color': '#000000', 'text-decoration-color': '#000000',
  'font-family': "'Montserrat', sans-serif", 'font-size': '16px', 'line-height': '1.4', 'letter-spacing': '0px',
  'border-bottom': '1px solid #dddddd', border: '1px solid #dddddd', 'border-radius': '6px', padding: '8px', margin: '0px',
  'box-shadow': '0 2px 6px rgba(0,0,0,0.15)', 'text-shadow': '1px 1px 2px rgba(0,0,0,0.3)', opacity: '1'
}

// Edits the custom CSS (foods.item_css / item_name_css / item_description_css) of the selected items,
// one row per property. Changes show live on the menu; Cancel restores the saved styles.
export default {
  components: { ErrorNotice },
  name: 'CustomStylesEditor',
  props: ['selected_item_details', 'menu'],
  data() {
    const source = this.selected_item_details.find(item => item.custom_css_styles) || this.selected_item_details[0] || {}
    return {
      sections: SECTIONS,
      rows: Object.fromEntries(SECTIONS.map(({ column }) => [column, declarations(source[column]).map(row => ({ ...row }))])),
      // what each item had, to restore on cancel
      original: this.selected_item_details.map(item => Object.fromEntries(SECTIONS.map(({ column }) => [column, item[column]]))),
      adding: Object.fromEntries(SECTIONS.map(({ column }) => [column, ''])),
      font_groups: [],
      saving: false,
      saved: false,
      error: ''
    }
  },
  created() {
    this.$http.secured.get(`/app_custom_css_fonts/${this.menu.id}`)
      .then(response => { this.font_groups = response.data.groups })
      .catch(() => { this.font_groups = [] })
  },
  beforeDestroy() {
    if (!this.saved) {
      this.restore() // closed some other way: don't leave unsaved changes showing
    }
  },
  methods: {
    isAllowedDeclaration,
    kind(property) {
      if (COLOR_PROPERTIES.includes(property)) return 'color'
      if (property === 'font-family') return 'font'
      return 'text'
    },
    choices(property) {
      return CHOICES[property] || null
    },
    propertyLabel(property) {
      return property.replace(/-/g, ' ').replace(/^./, letter => letter.toUpperCase())
    },
    hexValue(value) {
      return /^#[0-9a-fA-F]{6}$/.test(value) ? value : '#000000'
    },
    fontName(value) {
      return (value || '').split(',')[0].trim().replace(/^['"]|['"]$/g, '')
    },
    knownFont(value) {
      return this.font_groups.some(group => group.fonts.includes(this.fontName(value)))
    },
    setFont(column, row, font) {
      const group = this.font_groups.find(g => g.fonts.includes(font))
      this.setValue(column, row, `'${font}', ${group ? group.generic : 'sans-serif'}`)
    },
    availableProperties(column) {
      const used = this.rows[column].map(row => row.property)
      return ALLOWED_PROPERTIES.filter(property => !used.includes(property))
    },
    setValue(column, row, value) {
      row.value = value
      this.preview(column)
    },
    addRow(column) {
      const property = this.adding[column]
      this.rows[column].push({ property, value: DEFAULT_VALUES[property] || (CHOICES[property] ? CHOICES[property][0] : '') })
      this.adding[column] = ''
      this.preview(column)
    },
    removeRow(column, index) {
      this.rows[column].splice(index, 1)
      this.preview(column)
    },
    // Live preview: the selected items are the objects the menu renders, so updating them restyles the menu.
    preview(column) {
      const css = toCss(this.rows[column])
      this.selected_item_details.forEach(item => {
        item[column] = css
      })
      const fontRow = this.rows[column].find(row => row.property === 'font-family')
      if (fontRow) {
        loadFonts({ fontFamily: fontRow.value })
      }
    },
    restore() {
      this.selected_item_details.forEach((item, i) => {
        SECTIONS.forEach(({ column }) => { item[column] = this.original[i][column] })
      })
    },
    cancel() {
      this.restore()
      this.saved = true // nothing left to restore on destroy
      this.$emit('return-to-buttons')
    },
    save() {
      const css = Object.fromEntries(SECTIONS.map(({ column }) => [column, toCss(this.rows[column])]))
      this.error = ''
      this.saving = true
      this.$http.secured.post(`/app_custom_css/${this.menu.id}`, { food_ids: this.selected_item_details.map(item => item.id), ...css })
        .then(response => {
          // show exactly what the server stored
          this.selected_item_details.forEach(item => {
            SECTIONS.forEach(({ column }) => { item[column] = response.data[column] })
          })
          this.saved = true
          this.$emit('items-updated', JSON.stringify(this.selected_item_details))
          this.$emit('return-to-buttons')
        })
        .catch(error => {
          this.error = (error.response && error.response.data && error.response.data.error) || "Couldn't save the styles. Please try again."
        })
        .finally(() => { this.saving = false })
    }
  }
}
</script>


<style scoped>

#custom-styles-editor {
  box-sizing: border-box;
  width: 100%;
  max-height: 70vh;
  overflow-y: auto;
  padding: 0px 20px 10px 20px;
  color: #2F2F2E;
}

.top-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.back-button {
  cursor: pointer;
}

.back-button img {
  width: 20px;
  height: 20px;
}

h3 {
  font-size: 20px;
  font-weight: 400;
  margin: 10px 0px;
}

.explainer {
  font-size: 12px;
  color: dimgray;
  margin: 0px 0px 10px 0px;
  text-align: center;
}

.section {
  margin-bottom: 20px;
}

.section-title {
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: dimgray;
  margin: 0px 0px 8px 0px;
}

.empty {
  font-size: 12px;
  color: dimgray;
  margin: 0px 0px 8px 0px;
}

.row {
  display: grid;
  grid-template-columns: 140px 1fr;
  align-items: center;
  gap: 4px 10px;
  padding: 6px 0px;
  border-bottom: 1px solid #f0f0f0;
}

.row label {
  font-size: 13px;
}

.control {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  min-width: 0;
}

.control input[type="text"], .control select {
  flex: 1;
  min-width: 0;
  font-size: 13px;
  font-family: 'Montserrat', sans-serif;
  padding: 5px 6px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  background: white;
}

.control input[type="color"] {
  width: 32px;
  height: 28px;
  padding: 0px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  flex-shrink: 0;
}

.remove {
  width: 12px;
  height: 12px;
  cursor: pointer;
  flex-shrink: 0;
}

.row-invalid input[type="text"] {
  border-color: #BE1E2D;
}

.invalid-note {
  grid-column: 2;
  font-size: 11px;
  color: #BE1E2D;
  margin: 0px;
}

.add-row {
  display: flex;
  flex-direction: row;
  gap: 8px;
  margin-top: 8px;
}

.add-row select {
  font-size: 13px;
  font-family: 'Montserrat', sans-serif;
  padding: 5px 6px;
  border: 1px solid #dddddd;
  border-radius: 4px;
  background: white;
}

button {
  font-family: 'Montserrat', sans-serif;
  font-size: 13px;
  font-weight: 600;
  padding: 8px 16px;
  border-radius: 4px;
  border: 1px solid rgba(39, 170, 225, 1);
  background: rgba(39, 170, 225, 0.15);
  color: #276175;
  cursor: pointer;
}

button.secondary {
  border-color: #cccccc;
  background: white;
  color: #505050;
}

.error {
  color: #BE1E2D;
  font-size: 13px;
  font-weight: 600;
}

.actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  gap: 10px;
  position: sticky;
  bottom: 0;
  background: white;
  padding: 10px 0px;
}

@media(max-width: 600px) {
  .row {
    grid-template-columns: 1fr;
  }
  .invalid-note {
    grid-column: 1;
  }
}

</style>
